import React, { useMemo } from 'react'
import { css } from '@emotion/core'
import { colors } from '@fs/zion-ui'

const containerCss = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: 0 auto;
`

/**
 * Object made up of region codes and their record count
 * @typedef {Object} RegionData
 * @property {string} name
 * @property {string} code
 * @property {number} recordCount
 * @property {number} recordRank
 * @property {number} populationCount
 * @property {number} populationRank
 * @property {string|undefined} colorOverride
 */

/**
 * Returns the color associated with a region based on its rank among the top five regions with records.
 *
 * @param {number} rank - A number indicating the rank of the region (1: top 1-2, 2: top 3-5, 3: remaining)
 * @param {string} mapColor - Default map color
 * @param {string|undefined} colorOverride - Optional color override for the region
 * @param {string[]} rankColors - Array of colors for ranks 1, 2, and 3
 */
export const getRegionColor = (rank, mapColor, colorOverride, rankColors) => {
  if (colorOverride) {
    return colorOverride
  }
  switch (rank) {
    case 1:
      return rankColors[0] || colors.red50
    case 2:
      return rankColors[1] || colors.green20
    case 3:
      return rankColors[2] || colors.blue30
    default:
      return mapColor
  }
}

/**
 * Returns an object with region codes as properties, and colors as values
 *
 * @param {RegionData[]} regions
 * @returns {{[regionCode: string]: string}}
 */
export const getRegionColors = (regions, rankAttribute, mapColor, rankColors) => {
  const regionColors = {}
  regions?.forEach((region) => {
    regionColors[region.code] = getRegionColor(region[rankAttribute], mapColor, region.colorOverride, rankColors)
  })
  return regionColors
}

// RegionalMap component for when regional data has high detail (i.e. Philippines), where each province object follows the RegionData structure
export default function AdvancedRegionalMap({
  regions,
  mapColor,
  maxWidth = 346,
  rankAttribute = 'recordRank',
  rankColors = [],
  children,
}) {
  const regionColors = useMemo(
    () => getRegionColors(regions, rankAttribute, mapColor, rankColors),
    [regions, rankAttribute, mapColor, rankColors]
  )

  return (
    <div css={containerCss} data-testid="regional-map-container" style={{ maxWidth }}>
      {React.Children.map(children, (child) =>
        React.isValidElement(child) ? React.cloneElement(child, { regionColors, mapColor }) : child
      )}
    </div>
  )
}
